/**
 * Canonical URL and public site origin helpers (Milestone 14, M8 mobility identity).
 */
import { resolveCanonicalVehiclePublicUrl, toPublicVehicleLinkInput } from "../utils/automapListing/resolveCanonicalVehiclePublicUrl";
import { AUTOMAPA_DEFAULT_OG_IMAGE, CANONICAL_STRIP_PARAMS, PUBLIC_VEHICLE_DETAIL_PREFIX } from "./seoConstants";

const ENV_SITE_URL_KEYS = [
  "REACT_APP_SITE_URL",
  "VITE_SITE_URL",
  "NEXT_PUBLIC_SITE_URL",
  "PUBLIC_SITE_URL",
];

let cachedOrigin = null;

function readConfiguredSiteUrl() {
  if (typeof process !== "undefined" && process.env) {
    for (const key of ENV_SITE_URL_KEYS) {
      const value = process.env[key];
      if (value?.trim()) return value.trim().replace(/\/+$/, "");
    }
  }
  return null;
}

/**
 * Public site origin for canonical/OG URLs.
 * TODO(M14): Set REACT_APP_SITE_URL in production to the real AutoMapa domain.
 */
export function getPublicSiteOrigin() {
  if (cachedOrigin) return cachedOrigin;

  const configured = readConfiguredSiteUrl();
  if (configured) {
    cachedOrigin = configured;
    return cachedOrigin;
  }

  if (typeof window !== "undefined" && window.location?.origin) {
    const origin = window.location.origin.replace(/\/+$/, "");
    if (process.env.NODE_ENV === "production" && /localhost|127\.0\.0\.1/i.test(origin)) {
      // Avoid localhost in production canonical tags when env is missing.
      return "";
    }
    cachedOrigin = origin;
    return cachedOrigin;
  }

  return "";
}

export function normalizePath(path = "/") {
  if (!path) return "/";
  const trimmed = String(path).split("?")[0].replace(/\/+$/, "") || "/";
  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
}

export function stripTrackingParams(input) {
  if (!input) return new URLSearchParams();

  const sp =
    input instanceof URLSearchParams
      ? new URLSearchParams(input.toString())
      : new URLSearchParams(String(input).replace(/^\?/, ""));

  for (const key of [...sp.keys()]) {
    const lower = key.toLowerCase();
    if (CANONICAL_STRIP_PARAMS.has(lower) || lower.startsWith("utm_")) {
      sp.delete(key);
    }
  }

  return sp;
}

export function buildCanonicalUrl(path, params = null) {
  const basePath = normalizePath(path);
  if (!params) return basePath;

  const sp = stripTrackingParams(params);
  const qs = sp.toString();
  return qs ? `${basePath}?${qs}` : basePath;
}

export function buildAbsoluteUrl(pathOrUrl) {
  if (!pathOrUrl) return undefined;
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl.replace(/\/+$/, pathOrUrl.endsWith("/") ? "/" : "");

  const origin = getPublicSiteOrigin();
  const path = buildCanonicalUrl(pathOrUrl);
  if (!origin) return path;
  return `${origin}${path.startsWith("/") ? path : `/${path}`}`;
}

export function getPublicVehicleDetailPath(listing, { locale = null, slug = "automap", manifest = null } = {}) {
  if (!listing) return null;
  const identity = toPublicVehicleLinkInput(listing);
  const path = resolveCanonicalVehiclePublicUrl({
    canonicalPublicUrl: identity.canonicalPublicUrl,
    mobilityListingId: identity.mobilityListingId,
    locale,
    slug,
    manifest,
  });
  if (path) return path;

  // Legacy slug route retained for preferPublicCanonical navigation only.
  const segment = listing.slug || null;
  if (!segment) return null;
  return `${PUBLIC_VEHICLE_DETAIL_PREFIX}/${encodeURIComponent(segment)}`;
}

export function resolveOgImageUrl(imageUrl) {
  const fallback = buildAbsoluteUrl(AUTOMAPA_DEFAULT_OG_IMAGE) || AUTOMAPA_DEFAULT_OG_IMAGE;
  if (!imageUrl || typeof imageUrl !== "string") return fallback;

  const trimmed = imageUrl.trim();
  if (!trimmed || trimmed.includes("vehicle-placeholder")) return fallback;

  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const parsed = new URL(trimmed);
      if (!parsed.protocol.startsWith("http")) return fallback;
      return trimmed;
    } catch {
      return fallback;
    }
  }

  if (trimmed.startsWith("/")) {
    return buildAbsoluteUrl(trimmed) || trimmed;
  }

  return fallback;
}

/** Test helper — reset cached origin between tests. */
export function resetPublicSiteOriginCache() {
  cachedOrigin = null;
}
